import React, { useState, useEffect } from 'react';
import { Play, Settings, Star, X, Lock, Crown, LayoutDashboard } from 'lucide-react';
import { DIMENSIONS, DIMENSION_DESCRIPTIONS } from '../../constants/gameConstants';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAnonymous } from '../../contexts/AnonymousContext';
import { useAuth } from '../../contexts/AuthContext';
import UpgradeModal from '../UpgradeModal';
import { colors, typography, spacing, radius, components, breakpoints } from '../../../styles/theme';

const SettingsPanel = ({
  activeDimensions,
  toggleDimension,
  nValue,
  setNValue,
  interferenceRate,
  setInterferenceRate,
  startGame,
  voiceMap,
  getLevelForCombo,
  updateLevelForCombo,
  trainedCombos = [],
  setActiveDimensions,
  favorites = [],
  toggleFavorite,
  isAuthenticated = false,
  hasRecentSession = false
}) => {
  // Get tier info for dimension locking and usage limits
  const { 
    tier, 
    tierRules, 
    canAccessDimension, 
    validateDimensions, 
    upgradeAccount,
    usageInfo
  } = useSubscription();
  
  // Get auth info
  const { isAuthenticated: authIsAuthenticated } = useAuth();
  const isAuth = isAuthenticated || authIsAuthenticated;
  
  // Get anonymous usage info
  const { roundsToday: anonRoundsToday, dailyLimit: anonDailyLimit, hasReachedLimit: anonLimitReached } = useAnonymous();
  
  // Modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Responsive state
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const mobile = windowWidth < parseInt(breakpoints.mobile);
  
  // Map single letter codes to dimension names (must match server/src/utils/dimensions.js)
  const codeToMapping = {
    'P': 'position', 'A': 'audio', 'V': 'voice', 'M': 'note',
    'C': 'color', 'S': 'shape', 'N': 'number', 'F': 'face',
    'R': 'arrow', 'O': 'movement', 'T': 'pattern', 'B': 'border',
    'L': 'orbitalPosition', 'Z': 'circleSize', 'X': 'circleRotation', 'I': 'circleBrightness',
    'W': 'categoryText', 'E': 'categoryAudio', 'Y': 'nameText', 'U': 'nameAudio', 'Q': 'math', 'H': 'rhythm', 'D': 'mandarinTone', 'G': 'volume'
  };
  
  // Reverse mapping: dimension name to code
  const dimensionToCode = {};
  Object.entries(codeToMapping).forEach(([code, dim]) => {
    dimensionToCode[dim] = code;
  });

  const handleSelectCombo = (combo) => {
    const dimensions = combo.comboCode.split('').map(code => codeToMapping[code]).filter(Boolean);
    setActiveDimensions(dimensions);
  };

  const getDimensionLabel = (comboCode) => {
    const dimensions = comboCode.split('').map(code => codeToMapping[code]).filter(Boolean);
    return dimensions.map(dim => {
      const label = DIMENSIONS[dim]?.name || dim;
      return label;
    }).join(' + ');
  };
  
  const getCurrentComboCode = () => {
    // Map dimensions to codes first, then sort the codes alphabetically
    // This matches the server's getDimensionComboCode() behavior
    return [...activeDimensions]
      .map(dim => dimensionToCode[dim])
      .filter(Boolean)
      .sort()
      .join('');
  };
  
  const getCurrentComboName = () => {
    return activeDimensions.map(dim => DIMENSIONS[dim]?.name || dim).join(' + ');
  };
  
  const isCurrentComboFavorited = () => {
    const currentCode = getCurrentComboCode();
    return favorites.some(f => f.comboCode === currentCode);
  };
  
  const handleToggleFavorite = () => {
    const currentCode = getCurrentComboCode();
    const currentName = getCurrentComboName();
    toggleFavorite(currentCode, currentName);
  };
  
  // Check if current combo has training history
  const currentComboHasHistory = () => {
    const currentCode = getCurrentComboCode();
    return trainedCombos.some(combo => combo.comboCode === currentCode);
  };
  
  // Check if a trained combo matches the current selection
  const isComboSelected = (comboCode) => {
    const currentCode = getCurrentComboCode();
    return currentCode === comboCode;
  };

  // Check if a combo is allowed for current tier
  const isComboAllowed = (comboCode) => {
    const dimensions = comboCode.split('').map(code => codeToMapping[code]).filter(Boolean);
    
    // Check if combo has too many dimensions
    if (dimensions.length > tierRules.maxDimensions) {
      return false;
    }
    
    // Check if all dimensions are allowed
    return dimensions.every(dim => canAccessDimension(dim));
  };

  // Filter favorites to only show tier-allowed combos
  const allowedFavorites = favorites.filter(fav => isComboAllowed(fav.comboCode));

  // Filter trained combos to only show tier-allowed combos
  const allowedTrainedCombos = trainedCombos.filter(combo => isComboAllowed(combo.comboCode));

  // Check if current selection is valid for tier
  const isCurrentSelectionValid = () => {
    if (activeDimensions.length === 0) return false;
    if (activeDimensions.length > tierRules.maxDimensions) return false;
    return activeDimensions.every(dim => canAccessDimension(dim));
  };

  return (
    <div style={{
      ...components.card,
      marginBottom: 0,
      fontFamily: typography.fontSans,
      padding: mobile ? spacing[4] : spacing[8],
      maxHeight: mobile ? 'calc(100vh - 70px)' : 'none',
      overflowY: mobile ? 'auto' : 'visible'
    }}>
      {/* Back to Dashboard Button - At the top (only for authenticated users) */}
      {isAuth && (
        <button
          onClick={() => window.location.href = '/dashboard'}
          style={{
            width: '100%',
            background: 'rgba(100, 116, 139, 0.15)',
            border: '1px solid rgba(100, 116, 139, 0.3)',
            borderRadius: radius.md,
            padding: mobile ? spacing[2] : spacing[3],
            fontSize: mobile ? typography.xs : typography.sm,
            fontWeight: typography.semibold,
            color: colors.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing[2],
            transition: 'all 0.2s ease',
            marginBottom: spacing[4],
            fontFamily: typography.fontSans
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(100, 116, 139, 0.25)';
            e.target.style.borderColor = 'rgba(100, 116, 139, 0.5)';
            e.target.style.color = colors.textPrimary;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(100, 116, 139, 0.15)';
            e.target.style.borderColor = 'rgba(100, 116, 139, 0.3)';
            e.target.style.color = colors.textSecondary;
          }}
        >
          <LayoutDashboard size={mobile ? 14 : 16} />
          Back to Dashboard
        </button>
      )}

      {/* Header - Clean and minimal */}
      <div style={{
        marginBottom: mobile ? spacing[4] : spacing[8]
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: mobile ? typography.xl : typography['2xl'],
          fontWeight: typography.bold,
          color: colors.textPrimary,
          letterSpacing: '-0.02em',
          lineHeight: typography.tight,
          marginBottom: spacing[2]
        }}>
          Configure Training
        </h2>
        <p style={{
          margin: 0,
          fontSize: mobile ? typography.xs : typography.sm,
          color: colors.textTertiary,
          lineHeight: typography.normal
        }}>
          Select dimensions and level to begin your session
        </p>
      </div>

      {/* Progress Tracker */}
      {tier === 'free' && usageInfo && (
        <div style={{
          background: 'rgba(96, 165, 250, 0.1)',
          border: `1px solid rgba(96, 165, 250, 0.3)`,
          borderRadius: radius.md,
          padding: spacing[4],
          marginBottom: spacing[5],
          fontFamily: typography.fontSans
        }}>
          <div style={{
            fontSize: typography.sm,
            fontWeight: typography.semibold,
            color: colors.primary,
            marginBottom: spacing[2]
          }}>
            Rounds Today: {usageInfo.roundsToday} of {tierRules.dailyRounds}
          </div>
          <div style={{
            display: 'flex',
            gap: spacing[1],
            marginBottom: spacing[2]
          }}>
            {[...Array(tierRules.dailyRounds)].map((_, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: radius.sm,
                  background: i < usageInfo.roundsToday 
                    ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                    : 'rgba(100, 116, 139, 0.2)',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
          <div style={{
            fontSize: typography.xs,
            color: colors.textTertiary
          }}>
            {usageInfo.remainingToday > 0 
              ? `${usageInfo.remainingToday} rounds remaining today`
              : 'Daily limit reached'}
          </div>
        </div>
      )}

      {tier === 'paid' && (
        <div style={{
          background: 'rgba(251, 191, 36, 0.1)',
          border: `1px solid rgba(251, 191, 36, 0.3)`,
          borderRadius: radius.md,
          padding: spacing[4],
          marginBottom: spacing[5],
          fontFamily: typography.fontSans,
          display: 'flex',
          alignItems: 'center',
          gap: spacing[2]
        }}>
          <span style={{ fontSize: typography.lg }}>🚀</span>
          <div style={{
            fontSize: typography.sm,
            fontWeight: typography.semibold,
            color: colors.warning
          }}>
            Pro Member - Unlimited Play!
          </div>
        </div>
      )}

      {/* Start Training Round Button - Primary CTA */}
      <button
        onClick={() => {
          if (!isCurrentSelectionValid()) {
            if (activeDimensions.length === 0) {
              alert('Please select at least one dimension to start training.');
            } else if (activeDimensions.length > tierRules.maxDimensions) {
              alert(`Maximum ${tierRules.maxDimensions} dimensions allowed for ${tier} tier.\n\nUpgrade to Pro for unlimited combinations!`);
            } else {
              const disallowed = activeDimensions.filter(dim => !canAccessDimension(dim));
              alert(`These dimensions are not allowed for ${tier} tier: ${disallowed.join(', ')}\n\nUpgrade to Pro to unlock all 24 dimensions!`);
            }
            return;
          }
          
          // Check usage limits for anonymous users
          if (!isAuthenticated) {
            if (anonLimitReached || anonRoundsToday >= anonDailyLimit) {
              if (confirm(`You've completed your ${anonDailyLimit} free trial rounds today!\n\nSign up for a free account to get ${tierRules.dailyRounds} rounds per day, track your progress, and more.\n\nClick OK to sign up now.`)) {
                window.location.href = '/signup';
              }
              return;
            }
          }
          
          // Check usage limits for free tier
          if (tier === 'free') {
            // Check daily limit
            if (usageInfo && usageInfo.remainingToday <= 0) {
              if (confirm(`You've reached your daily limit of ${tierRules.dailyRounds} rounds.\n\nUpgrade to Pro for unlimited rounds?\n\nClick OK to upgrade, or Cancel to wait until tomorrow.`)) {
                setShowUpgradeModal(true);
              }
              return;
            }
          }
          
          // All checks passed, start the game
          startGame();
        }}
        disabled={!isCurrentSelectionValid()}
        style={{
          width: '100%',
          background: !isCurrentSelectionValid()
            ? colors.bgInput
            : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          border: 'none',
          borderRadius: radius.lg,
          padding: spacing[5],
          fontSize: typography.lg,
          fontWeight: typography.bold,
          color: !isCurrentSelectionValid() ? colors.textMuted : 'white',
          cursor: !isCurrentSelectionValid() ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: spacing[3],
          transition: 'all 0.2s ease',
          marginBottom: spacing[3],
          fontFamily: typography.fontSans,
          boxShadow: isCurrentSelectionValid() ? '0 4px 12px rgba(96, 165, 250, 0.2)' : 'none'
        }}
        onMouseEnter={(e) => {
          if (isCurrentSelectionValid()) {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 6px 16px rgba(96, 165, 250, 0.3)';
          }
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = isCurrentSelectionValid() ? '0 4px 12px rgba(96, 165, 250, 0.2)' : 'none';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3] }}>
          <Play size={20} fill="currentColor" />
          {hasRecentSession ? 'Continue Training' : 'Start Training'}
        </div>
        {activeDimensions.length > 0 && (
          <div style={{
            fontSize: typography.base,
            fontWeight: typography.semibold,
            opacity: 0.95,
            background: 'rgba(255, 255, 255, 0.15)',
            padding: `${spacing[1]} ${spacing[3]}`,
            borderRadius: radius.sm
          }}>
            {nValue}-Back
          </div>
        )}
      </button>

      {/* Favorite Dimension Combinations */}
      {isAuthenticated && allowedFavorites.length > 0 && (
        <div style={{
          background: colors.warningBg,
          border: `1px solid ${colors.warningBorder}`,
          borderRadius: radius.md,
          padding: spacing[4],
          marginBottom: spacing[5]
        }}>
          <div style={{
            fontSize: typography.sm,
            color: colors.warning,
            fontWeight: typography.semibold,
            marginBottom: spacing[3],
            display: 'flex',
            alignItems: 'center',
            gap: spacing[2]
          }}>
            <Star size={14} fill={colors.warning} />
            Starred
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: spacing[2]
          }}>
            {allowedFavorites.map(fav => (
              <div
                key={fav.comboCode}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[2],
                  background: colors.warningBg,
                  border: `1px solid ${colors.warningBorder}`,
                  borderRadius: radius.sm,
                  padding: `${spacing[2]} ${spacing[3]}`,
                  fontFamily: typography.fontSans,
                  fontSize: typography.sm,
                  fontWeight: typography.medium
                }}
              >
                <button
                  onClick={() => handleSelectCombo(fav)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: colors.textPrimary,
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing[2],
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    fontWeight: 'inherit'
                  }}
                >
                  <Star size={12} fill={colors.warning} color={colors.warning} />
                  {fav.comboName}
                </button>
                <button
                  onClick={() => toggleFavorite(fav.comboCode, fav.comboName)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: colors.textTertiary,
                    cursor: 'pointer',
                    padding: spacing[1],
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'color 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = colors.error;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = colors.textTertiary;
                  }}
                  title="Remove from favorites"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Previously Trained Combos */}
      {allowedTrainedCombos.length > 0 && (
        <div style={{
          background: colors.info,
          border: `1px solid ${colors.infoBorder}`,
          borderRadius: radius.md,
          padding: spacing[4],
          marginBottom: spacing[5]
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: spacing[3]
          }}>
            <div style={{
              fontSize: typography.sm,
              color: colors.infoText,
              fontWeight: typography.semibold,
              display: 'flex',
              alignItems: 'center',
              gap: spacing[2]
            }}>
              ⚡ Continue Training
            </div>
            {/* Star/Favorite Button (compact, inline) */}
            {isAuthenticated && currentComboHasHistory() && (
              <button
                onClick={handleToggleFavorite}
                style={{
                  background: isCurrentComboFavorited() 
                    ? colors.warningBg
                    : 'rgba(100, 116, 139, 0.15)',
                  border: `1px solid ${isCurrentComboFavorited() ? colors.warningBorder : colors.border}`,
                  borderRadius: radius.sm,
                  padding: `${spacing[2]} ${spacing[3]}`,
                  fontSize: typography.xs,
                  fontWeight: typography.semibold,
                  color: isCurrentComboFavorited() ? colors.warning : colors.textSecondary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[2],
                  transition: 'all 0.15s ease',
                  fontFamily: typography.fontSans
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = isCurrentComboFavorited() 
                    ? 'rgba(251, 191, 36, 0.2)'
                    : 'rgba(100, 116, 139, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = isCurrentComboFavorited() 
                    ? colors.warningBg
                    : 'rgba(100, 116, 139, 0.15)';
                }}
              >
                <Star 
                  size={12} 
                  fill={isCurrentComboFavorited() ? colors.warning : 'none'}
                  color={isCurrentComboFavorited() ? colors.warning : colors.textSecondary}
                />
                {isCurrentComboFavorited() ? 'Starred' : 'Star'}
              </button>
            )}
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: spacing[2]
          }}>
            {allowedTrainedCombos.slice().sort((a, b) => {
              return new Date(b.lastTrained) - new Date(a.lastTrained);
            }).map(combo => {
              const isSelected = isComboSelected(combo.comboCode);
              return (
                <button
                  key={combo.comboCode}
                  onClick={() => handleSelectCombo(combo)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    background: isSelected 
                      ? 'rgba(96, 165, 250, 0.2)'
                      : colors.info,
                    border: isSelected
                      ? `2px solid ${colors.primary}`
                      : `1px solid ${colors.infoBorder}`,
                    borderRadius: radius.md,
                    padding: isSelected ? spacing[3] : spacing[3],
                    color: colors.textPrimary,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    textAlign: 'left',
                    fontFamily: typography.fontSans,
                    gap: spacing[1]
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = 'rgba(96, 165, 250, 0.15)';
                      e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = colors.info;
                      e.currentTarget.style.borderColor = colors.infoBorder;
                    }
                  }}
                >
                  <div style={{ 
                    fontWeight: typography.semibold, 
                    fontSize: typography.sm,
                    lineHeight: typography.tight,
                    color: isSelected ? colors.primary : colors.textPrimary
                  }}>
                    {getDimensionLabel(combo.comboCode)}
                  </div>
                  <div style={{ 
                    fontSize: typography.xs, 
                    lineHeight: typography.tight,
                    color: isSelected ? colors.primaryLight : colors.textTertiary,
                    fontWeight: typography.medium
                  }}>
                    Level {combo.currentLevel || 'N/A'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tier Limits Info - Inline, super subtle */}
      {tier !== 'paid' && (
        <div style={{
          fontSize: typography.xs,
          color: colors.textTertiary,
          fontFamily: typography.fontSans,
          marginBottom: spacing[5],
          padding: `${spacing[2]} ${spacing[3]}`,
          background: 'rgba(100, 116, 139, 0.05)',
          borderLeft: `2px solid ${colors.border}`,
          borderRadius: '0 4px 4px 0'
        }}>
          {tier === 'free' ? 'Free tier' : 'Trial'} — Select up to {tierRules.maxDimensions} dimensions at a time between
          {tier === 'free' && ' Position, Color, and Audio'}
          {tier === 'anonymous' && ' Position, and Audio'}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: mobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: mobile ? spacing[2] : spacing[4],
        marginBottom: mobile ? spacing[4] : spacing[8]
      }}>
        {Object.entries(DIMENSIONS).map(([key, dim]) => {
          const isLocked = !canAccessDimension(key);
          const isActive = activeDimensions.includes(key);
          const tooManySelected = activeDimensions.length >= tierRules.maxDimensions && !isActive;
          
          return (
            <button
              key={key}
              onClick={() => {
                if (isLocked) {
                  // Show upgrade prompt for locked dimensions
                  if (window.confirm(`${dim.name} is only available for Pro users.\n\nUpgrade now to unlock all 24 dimensions?`)) {
                    setShowUpgradeModal(true);
                  }
                } else if (tooManySelected) {
                  // Show max dimensions message
                  alert(`Maximum ${tierRules.maxDimensions} dimensions allowed for ${tier} tier.\n\nDeselect a dimension first, or upgrade to Pro for unlimited combinations!`);
                } else {
                  toggleDimension(key);
                }
              }}
              style={{
                background: isLocked
                  ? 'rgba(30, 30, 30, 0.3)'
                  : isActive
                    ? `linear-gradient(135deg, ${dim.color}25 0%, ${dim.color}10 100%)`
                    : 'rgba(30, 41, 59, 0.3)',
                border: isLocked
                  ? `1px solid rgba(100, 100, 100, 0.2)`
                  : `2px solid ${isActive ? dim.color : colors.borderLight}`,
                borderRadius: radius.lg,
                padding: spacing[4],
                cursor: isLocked ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                color: isLocked ? colors.textMuted : colors.textPrimary,
                textAlign: 'left',
                position: 'relative',
                opacity: isLocked ? 0.4 : 1,
                fontFamily: typography.fontSans,
                boxShadow: isActive ? `0 0 0 1px ${dim.color}20` : 'none'
              }}
              onMouseEnter={(e) => {
                if (!isLocked) {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = isActive 
                    ? `0 4px 12px ${dim.color}30, 0 0 0 1px ${dim.color}30`
                    : '0 4px 12px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLocked) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isActive ? `0 0 0 1px ${dim.color}20` : 'none';
                }
              }}
            >
              {isLocked && (
                <div style={{
                  position: 'absolute',
                  top: spacing[2],
                  right: spacing[2],
                  background: 'rgba(251, 191, 36, 0.15)',
                  borderRadius: radius.full,
                  padding: spacing[1],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={10} color={colors.warning} />
                </div>
              )}
              <div style={{ 
                fontSize: typography.xs, 
                color: isLocked ? colors.textMuted : colors.textTertiary, 
                marginBottom: spacing[2],
                fontWeight: typography.medium,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {isLocked ? (
                  <span style={{ color: colors.warning, fontWeight: typography.semibold }}>
                    Pro
                  </span>
                ) : (
                  `${dim.key.toUpperCase()}`
                )}
              </div>
              <div style={{ 
                fontSize: typography.base, 
                fontWeight: typography.semibold, 
                marginBottom: spacing[2],
                color: isLocked ? colors.textMuted : colors.textPrimary,
                lineHeight: typography.tight
              }}>
                {dim.name}
              </div>
              <div style={{ 
                fontSize: typography.xs, 
                color: colors.textTertiary, 
                lineHeight: typography.snug
              }}>
                {DIMENSION_DESCRIPTIONS[key]}
              </div>
            </button>
          );
        })}
      </div>

      {(activeDimensions.includes('audio') || activeDimensions.includes('voice') || activeDimensions.includes('note')) && (
        <div style={{
          background: colors.info,
          border: `1px solid ${colors.infoBorder}`,
          borderRadius: radius.md,
          padding: spacing[4],
          marginBottom: spacing[5],
          fontSize: typography.sm,
          color: colors.textSecondary,
          fontFamily: typography.fontSans
        }}>
          <strong style={{ color: colors.infoText, fontWeight: typography.semibold }}>Auditory Dimensions:</strong>
          {' '}
          {activeDimensions.includes('note') && (
            <span>Musical notes (C4-C5) will play.</span>
          )}
          {activeDimensions.includes('audio') && activeDimensions.includes('voice') && (
            <span>Both letter AND speaker voice will vary.</span>
          )}
          {activeDimensions.includes('audio') && !activeDimensions.includes('voice') && !activeDimensions.includes('note') && (
            <span>Letter will vary, same speaker voice.</span>
          )}
          {!activeDimensions.includes('audio') && activeDimensions.includes('voice') && !activeDimensions.includes('note') && (
            <span>Speaker voice will vary (each says "this voice").</span>
          )}
          {activeDimensions.includes('voice') && Object.keys(voiceMap).length > 0 && (
            <div style={{ marginTop: spacing[2], fontSize: typography.xs, color: colors.textTertiary }}>
              <div>Speakers: {voiceMap.male1?.name || 'Loading...'}, {voiceMap.male2?.name || 'Loading...'}, {voiceMap.female1?.name || 'Loading...'}, {voiceMap.female2?.name || 'Loading...'}</div>
            </div>
          )}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
        gap: mobile ? spacing[4] : spacing[6],
        marginBottom: mobile ? spacing[4] : spacing[6]
      }}>
        {/* N-Back Level */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.3)',
          borderRadius: radius.lg,
          padding: spacing[6],
          fontFamily: typography.fontSans
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: spacing[4]
          }}>
            <label style={{
              fontWeight: typography.semibold,
              color: colors.textSecondary,
              fontSize: typography.sm,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              N-Back Level
            </label>
            <span style={{
              fontSize: typography['3xl'],
              fontWeight: typography.extrabold,
              color: colors.primary,
              letterSpacing: '-0.02em',
              lineHeight: typography.tight
            }}>
              {nValue}
            </span>
          </div>
          
          {/* Warning about overriding recommended level */}
          {activeDimensions.length > 0 && nValue !== getLevelForCombo(activeDimensions) && (
            <div style={{
              background: colors.warningBg,
              border: `1px solid ${colors.warningBorder}`,
              borderRadius: radius.sm,
              padding: spacing[3],
              marginBottom: spacing[4],
              fontSize: typography.xs,
              color: colors.warningText,
              lineHeight: typography.normal
            }}>
              <strong>Recommended: {getLevelForCombo(activeDimensions)}-back</strong>
            </div>
          )}
          
          <input
            type="range"
            min="1"
            max="24"
            value={nValue}
            onChange={(e) => {
              const newLevel = parseInt(e.target.value);
              setNValue(newLevel);
            }}
            style={{
              width: '100%',
              height: '4px',
              borderRadius: radius.sm,
              background: colors.border,
              outline: 'none',
              cursor: 'pointer',
              marginBottom: spacing[3]
            }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: typography.xs,
            color: colors.textTertiary,
            fontWeight: typography.medium
          }}>
            <span>1</span>
            <span>12</span>
            <span>24</span>
          </div>
        </div>

        {/* Interference Level */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.3)',
          borderRadius: radius.lg,
          padding: spacing[6],
          fontFamily: typography.fontSans
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: spacing[4]
          }}>
            <label style={{
              fontWeight: typography.semibold,
              color: colors.textSecondary,
              fontSize: typography.sm,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Interference
            </label>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing[2] }}>
              <span style={{
                fontSize: typography['3xl'],
                fontWeight: typography.extrabold,
                color: colors.primary,
                letterSpacing: '-0.02em',
                lineHeight: typography.tight
              }}>
                {Math.round(interferenceRate * 100)}
              </span>
              <span style={{
                fontSize: typography.xs,
                color: colors.textTertiary,
                fontWeight: typography.medium
              }}>
                %
              </span>
            </div>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(interferenceRate * 100)}
            onChange={(e) => setInterferenceRate(parseInt(e.target.value) / 100)}
            style={{
              width: '100%',
              height: '4px',
              borderRadius: radius.sm,
              background: colors.border,
              outline: 'none',
              cursor: 'pointer',
              marginBottom: spacing[3]
            }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: typography.xs,
            color: colors.textTertiary,
            fontWeight: typography.medium,
            marginBottom: spacing[3]
          }}>
            <span>None</span>
            <span>Medium</span>
            <span>High</span>
          </div>
          <p style={{
            margin: 0,
            fontSize: typography.xs,
            color: colors.textTertiary,
            lineHeight: typography.normal
          }}>
            Adds trick trials to increase difficulty
          </p>
        </div>
      </div>
      
      {/* Modals */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </div>
  );
};

export default SettingsPanel;

